// exportXls.ts — wrappers de export a Excel con lazy load de xlsx.
//
// Round 2026-04-29 — bundle optimization. Antes hacía
// `import * as XLSX from "xlsx"` estático, lo que forzaba a webpack a
// incluir los 279 KB minificados de xlsx en el main bundle, aunque el
// usuario nunca clickeara "Exportar a XLS". El SheetJS API es bastante
// pesado (todos los formatos, parsers, codecs).
//
// Ahora cargamos xlsx solo cuando se llama una de estas funciones —
// el primer click es ~200ms más lento (download del chunk + parse) pero
// el initial bundle baja ~280 KB. Para un usuario que NUNCA exporta,
// es 280 KB de menos descarga + parse en cada visita.
//
// Las funciones siguen siendo "fire-and-forget" para los callers — no
// retornan Promise resolveable, solo void async. xlsx.writeFile triggea
// el download del browser y no devuelve nada.

type AnyRow = Record<string, any>;

/**
 * Itera todas las páginas de un endpoint paginado y devuelve TODAS las
 * filas. Pensado para los botones "Exportar a XLS" de finanzas-empresa,
 * que antes solo exportaban la página actualmente visible (`data` del
 * useState) — bug reportado en arriendos round 2026-05-08.
 *
 * El caller pasa una función `fetchPage(page, pageSize)` que ya tiene
 * los filtros / período aplicados (closure sobre el state local de la
 * sección). Iteramos hasta que `data.length >= total` o hasta que la
 * página venga vacía. Cap defensivo de 100 páginas (50.000 filas con
 * pageSize=500) para no congelar el browser ante un endpoint roto.
 *
 * Tamaño de página grande (500) para minimizar round-trips. La mayoría
 * de las queries de finanzas devuelven cientos de filas en un mes, no
 * decenas de miles, así que en la práctica son 1-2 fetches.
 */
export async function fetchAllPages<T>(
  fetchPage: (
    page: number,
    pageSize: number,
  ) => Promise<{ data: T[]; total?: number } | null>,
  pageSize: number = 500,
): Promise<T[]> {
  const all: T[] = [];
  let page = 1;
  while (page <= 100) {
    const res = await fetchPage(page, pageSize);
    if (!res || !res.data?.length) break;
    all.push(...res.data);
    const total = res.total ?? all.length;
    if (all.length >= total) break;
    page++;
  }
  return all;
}

const toCellSafe = (v: any) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    return v;
  // arrays/objects -> texto
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

export async function exportToXlsxSingleSheet(opts: {
  filename: string;
  sheetName?: string;
  rows: AnyRow[];
  // opcional: reordenar/filtrar columnas
  columns?: { key: string; header?: string }[];
}): Promise<void> {
  const { filename, sheetName = "Datos", rows, columns } = opts;

  // Lazy load: webpack genera un chunk separado que se descarga acá.
  // Cache automático del navegador para clicks subsecuentes.
  const XLSX = await import("xlsx");

  const normalized = rows.map((r) => {
    if (!columns) {
      const out: AnyRow = {};
      for (const [k, v] of Object.entries(r)) out[k] = toCellSafe(v);
      return out;
    }
    const out: AnyRow = {};
    for (const c of columns) out[c.header ?? c.key] = toCellSafe(r[c.key]);
    return out;
  });

  const ws = XLSX.utils.json_to_sheet(normalized);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  XLSX.writeFile(
    wb,
    filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`
  );
}

export async function exportToXlsxMultiSheet(opts: {
  filename: string;
  sheets: { sheetName: string; rows: AnyRow[] }[];
}): Promise<void> {
  const { filename, sheets } = opts;

  const XLSX = await import("xlsx");

  const wb = XLSX.utils.book_new();

  for (const s of sheets) {
    const normalized = s.rows.map((r) => {
      const out: AnyRow = {};
      for (const [k, v] of Object.entries(r)) out[k] = toCellSafe(v);
      return out;
    });
    const ws = XLSX.utils.json_to_sheet(normalized);
    XLSX.utils.book_append_sheet(wb, ws, s.sheetName);
  }

  XLSX.writeFile(
    wb,
    filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`
  );
}
